import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
} from "react";
import { Link } from "react-router-dom";
import { getPasswordErrors, isStrongPassword } from "../../lib/passwordPolicy";
import { sha256Hex } from "../../lib/passwordHash";

const SESSION_KEY = "armeria_admin_session";
const USERS_KEY = "armeria_panel_users";
const SEEDED_ADMIN_EMAIL = "admin2@armeria47.com";
const SEEDED_ADMIN_PASSWORD = "Armeria#Admin2026$Segura";

type Role = "admin" | "editor" | "vendedor";

type StoredUser = {
  id: string;
  email: string;
  role: Role;
  passwordHash: string;
  createdAt: string;
};

const ROLE_INFO: Record<Role, string> = {
  admin: "Acceso completo al panel y gestión de usuarios.",
  editor: "Puede editar contenido del catálogo (cuando esté conectado al backend).",
  vendedor: "Consulta y cotizaciones; sin cambios críticos del sistema.",
};

function loadUsers(): StoredUser[] {
  try {
    const raw = localStorage.getItem(USERS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as StoredUser[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveUsers(users: StoredUser[]) {
  localStorage.setItem(USERS_KEY, JSON.stringify(users));
}

function panelPassword(): string {
  return import.meta.env.VITE_ADMIN_PANEL_PASSWORD ?? "ArmeriaPanel2026!";
}

async function ensureSeededAdmin(): Promise<void> {
  const users = loadUsers();
  const exists = users.some((u) => u.email === SEEDED_ADMIN_EMAIL);
  if (exists) return;

  const seededUser: StoredUser = {
    id: crypto.randomUUID(),
    email: SEEDED_ADMIN_EMAIL,
    role: "admin",
    passwordHash: await sha256Hex(SEEDED_ADMIN_PASSWORD),
    createdAt: new Date().toISOString(),
  };
  saveUsers([...users, seededUser]);
}

export default function AdminPage() {
  const [authed, setAuthed] = useState(false);
  const [loginPass, setLoginPass] = useState("");
  const [loginErr, setLoginErr] = useState("");

  const [users, setUsers] = useState<StoredUser[]>([]);

  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPassword2, setNewPassword2] = useState("");
  const [newRole, setNewRole] = useState<Role>("vendedor");
  const [createMsg, setCreateMsg] = useState("");

  const [chgEmail, setChgEmail] = useState("");
  const [chgPassword, setChgPassword] = useState("");
  const [chgPassword2, setChgPassword2] = useState("");
  const [chgMsg, setChgMsg] = useState("");

  useEffect(() => {
    const bootstrap = async () => {
      await ensureSeededAdmin();
      setAuthed(sessionStorage.getItem(SESSION_KEY) === "1");
      setUsers(loadUsers());
    };
    void bootstrap();
  }, []);

  const newPwdErrors = useMemo(() => getPasswordErrors(newPassword), [newPassword]);
  const chgPwdErrors = useMemo(() => getPasswordErrors(chgPassword), [chgPassword]);

  const handleLogin = (e: FormEvent) => {
    e.preventDefault();
    setLoginErr("");
    if (loginPass === panelPassword()) {
      sessionStorage.setItem(SESSION_KEY, "1");
      setAuthed(true);
      setLoginPass("");
    } else {
      setLoginErr("Contraseña incorrecta.");
    }
  };

  const logout = () => {
    sessionStorage.removeItem(SESSION_KEY);
    setAuthed(false);
  };

  const refreshUsers = useCallback(() => setUsers(loadUsers()), []);

  const handleCreateUser = async (e: FormEvent) => {
    e.preventDefault();
    setCreateMsg("");
    const email = newEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setCreateMsg("Correo no válido.");
      return;
    }
    if (newPassword !== newPassword2) {
      setCreateMsg("Las contraseñas no coinciden.");
      return;
    }
    if (!isStrongPassword(newPassword)) {
      setCreateMsg(newPwdErrors.join(" "));
      return;
    }
    const list = loadUsers();
    if (list.some((u) => u.email === email)) {
      setCreateMsg("Ya existe un usuario con ese correo.");
      return;
    }
    const passwordHash = await sha256Hex(newPassword);
    const u: StoredUser = {
      id: crypto.randomUUID(),
      email,
      role: newRole,
      passwordHash,
      createdAt: new Date().toISOString(),
    };
    list.push(u);
    saveUsers(list);
    refreshUsers();
    setNewEmail("");
    setNewPassword("");
    setNewPassword2("");
    setCreateMsg("Usuario creado correctamente.");
  };

  const handleChangePassword = async (e: FormEvent) => {
    e.preventDefault();
    setChgMsg("");
    const email = chgEmail.trim().toLowerCase();
    if (!email) {
      setChgMsg("Indica el correo del usuario.");
      return;
    }
    if (chgPassword !== chgPassword2) {
      setChgMsg("Las contraseñas nuevas no coinciden.");
      return;
    }
    if (!isStrongPassword(chgPassword)) {
      setChgMsg(chgPwdErrors.join(" "));
      return;
    }
    const list = loadUsers();
    const idx = list.findIndex((u) => u.email === email);
    if (idx === -1) {
      setChgMsg("No existe un usuario con ese correo.");
      return;
    }
    list[idx] = {
      ...list[idx],
      passwordHash: await sha256Hex(chgPassword),
    };
    saveUsers(list);
    refreshUsers();
    setChgEmail("");
    setChgPassword("");
    setChgPassword2("");
    setChgMsg("Contraseña actualizada.");
  };

  const removeUser = (id: string) => {
    if (!confirm("¿Eliminar este usuario del panel?")) return;
    const list = loadUsers().filter((u) => u.id !== id);
    saveUsers(list);
    refreshUsers();
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-zinc-950 text-white flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-md rounded-2xl border border-amber-500/30 bg-zinc-900/80 p-8 shadow-2xl">
          <h1 className="text-2xl font-black italic uppercase text-amber-500 mb-2">
            Panel administración
          </h1>
          <p className="text-zinc-400 text-sm mb-6">
            Acceso restringido. Define{" "}
            <code className="text-amber-500/90">VITE_ADMIN_PANEL_PASSWORD</code> en{" "}
            <code className="text-zinc-300">.env</code> para producción.
          </p>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">
                Contraseña del panel
              </label>
              <input
                type="password"
                value={loginPass}
                onChange={(e) => setLoginPass(e.target.value)}
                className="w-full bg-black/50 border border-zinc-700 rounded-xl px-4 py-3 outline-none focus:border-amber-500"
                autoComplete="current-password"
              />
            </div>
            {loginErr && (
              <p className="text-red-400 text-sm">{loginErr}</p>
            )}
            <button
              type="submit"
              className="w-full bg-amber-500 hover:bg-amber-400 text-black font-black py-3 rounded-xl uppercase tracking-widest"
            >
              Entrar
            </button>
          </form>
          <Link
            to="/"
            className="mt-6 block text-center text-sm text-zinc-500 hover:text-amber-500"
          >
            ← Volver al sitio
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white p-6 md:p-10">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-10">
          <div>
            <h1 className="text-3xl font-black italic uppercase text-amber-500">
              Administración
            </h1>
            <p className="text-zinc-500 text-sm mt-1">
              Usuarios del panel, roles y contraseñas seguras (almacenamiento local; conecta tu API para producción).
            </p>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={logout}
              className="px-4 py-2 rounded-xl border border-zinc-700 text-sm font-bold uppercase tracking-wider hover:bg-zinc-800"
            >
              Salir
            </button>
            <Link
              to="/"
              className="px-4 py-2 rounded-xl bg-amber-500 text-black text-sm font-black uppercase tracking-wider hover:bg-amber-400 flex items-center"
            >
              Sitio web
            </Link>
          </div>
        </div>

        <div className="rounded-3xl border border-amber-500/20 bg-zinc-900/50 p-6 md:p-8 space-y-10">
          <header>
            <h2 className="text-xl font-black uppercase tracking-wide text-white">
              Gestión de usuarios y seguridad
            </h2>
            <p className="text-zinc-500 text-sm mt-2">
              Crear cuentas, asignar roles y cambiar contraseñas. La contraseña nueva debe cumplir política fuerte
              (8+ caracteres, mayúscula, minúscula, número y símbolo).
            </p>
          </header>

          <div className="grid md:grid-cols-2 gap-8">
            <section className="rounded-2xl border border-zinc-800 bg-black/40 p-6">
              <h3 className="text-amber-500 font-black uppercase text-sm tracking-widest mb-4">
                Crear usuario
              </h3>
              <form onSubmit={handleCreateUser} className="space-y-4">
                <div>
                  <label className="text-xs font-bold text-zinc-500 uppercase">Correo</label>
                  <input
                    type="email"
                    required
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    className="mt-1 w-full bg-zinc-950 border border-zinc-700 rounded-xl px-3 py-2 outline-none focus:border-amber-500"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-zinc-500 uppercase">Rol</label>
                  <select
                    value={newRole}
                    onChange={(e) => setNewRole(e.target.value as Role)}
                    className="mt-1 w-full bg-zinc-950 border border-zinc-700 rounded-xl px-3 py-2 outline-none focus:border-amber-500"
                  >
                    <option value="admin">Administrador</option>
                    <option value="editor">Editor</option>
                    <option value="vendedor">Vendedor</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-zinc-500 uppercase">Contraseña</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="mt-1 w-full bg-zinc-950 border border-zinc-700 rounded-xl px-3 py-2 outline-none focus:border-amber-500"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-zinc-500 uppercase">Confirmar contraseña</label>
                  <input
                    type="password"
                    value={newPassword2}
                    onChange={(e) => setNewPassword2(e.target.value)}
                    className="mt-1 w-full bg-zinc-950 border border-zinc-700 rounded-xl px-3 py-2 outline-none focus:border-amber-500"
                  />
                </div>
                {newPassword.length > 0 && !isStrongPassword(newPassword) && (
                  <ul className="text-xs text-amber-200/80 list-disc pl-4 space-y-1">
                    {newPwdErrors.map((err) => (
                      <li key={err}>{err}</li>
                    ))}
                  </ul>
                )}
                <button
                  type="submit"
                  disabled={!isStrongPassword(newPassword)}
                  className="w-full bg-amber-500 hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed text-black font-black py-3 rounded-xl uppercase tracking-widest text-sm"
                >
                  Crear usuario
                </button>
                {createMsg && (
                  <p className={`text-sm ${createMsg.includes("correctamente") ? "text-green-400" : "text-amber-200"}`}>
                    {createMsg}
                  </p>
                )}
              </form>
            </section>

            <section className="rounded-2xl border border-zinc-800 bg-black/40 p-6">
              <h3 className="text-amber-500 font-black uppercase text-sm tracking-widest mb-4">
                Roles
              </h3>
              <ul className="space-y-3 text-sm text-zinc-300 mb-6">
                {(Object.keys(ROLE_INFO) as Role[]).map((r) => (
                  <li key={r}>
                    <span className="text-white font-bold capitalize">{r}:</span>{" "}
                    {ROLE_INFO[r]}
                  </li>
                ))}
              </ul>
              <h4 className="text-xs font-black uppercase text-zinc-500 mb-2">Usuarios registrados</h4>
              <div className="max-h-48 overflow-y-auto space-y-2 pr-1">
                {users.length === 0 ? (
                  <p className="text-zinc-600 text-sm">Ninguno aún.</p>
                ) : (
                  users.map((u) => (
                    <div
                      key={u.id}
                      className="flex items-center justify-between gap-2 bg-zinc-950/80 border border-zinc-800 rounded-lg px-3 py-2 text-sm"
                    >
                      <div className="min-w-0">
                        <p className="text-white font-medium truncate">{u.email}</p>
                        <p className="text-zinc-500 text-xs capitalize">{u.role}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeUser(u.id)}
                        className="text-red-400 text-xs font-bold uppercase shrink-0 hover:underline"
                      >
                        Quitar
                      </button>
                    </div>
                  ))
                )}
              </div>
            </section>
          </div>

          <section className="rounded-2xl border border-zinc-800 bg-black/40 p-6">
            <h3 className="text-amber-500 font-black uppercase text-sm tracking-widest mb-4">
              Cambiar contraseña de un usuario
            </h3>
            <form onSubmit={handleChangePassword} className="grid sm:grid-cols-2 gap-4 max-w-2xl">
              <div className="sm:col-span-2">
                <label className="text-xs font-bold text-zinc-500 uppercase">Correo del usuario</label>
                <input
                  type="email"
                  required
                  value={chgEmail}
                  onChange={(e) => setChgEmail(e.target.value)}
                  className="mt-1 w-full bg-zinc-950 border border-zinc-700 rounded-xl px-3 py-2 outline-none focus:border-amber-500"
                  list="user-emails"
                />
                <datalist id="user-emails">
                  {users.map((u) => (
                    <option key={u.id} value={u.email} />
                  ))}
                </datalist>
              </div>
              <div>
                <label className="text-xs font-bold text-zinc-500 uppercase">Nueva contraseña</label>
                <input
                  type="password"
                  value={chgPassword}
                  onChange={(e) => setChgPassword(e.target.value)}
                  className="mt-1 w-full bg-zinc-950 border border-zinc-700 rounded-xl px-3 py-2 outline-none focus:border-amber-500"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-zinc-500 uppercase">Confirmar</label>
                <input
                  type="password"
                  value={chgPassword2}
                  onChange={(e) => setChgPassword2(e.target.value)}
                  className="mt-1 w-full bg-zinc-950 border border-zinc-700 rounded-xl px-3 py-2 outline-none focus:border-amber-500"
                />
              </div>
              {chgPassword.length > 0 && !isStrongPassword(chgPassword) && (
                <div className="sm:col-span-2">
                  <ul className="text-xs text-amber-200/80 list-disc pl-4 space-y-1">
                    {chgPwdErrors.map((err) => (
                      <li key={err}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="sm:col-span-2">
                <button
                  type="submit"
                  disabled={!isStrongPassword(chgPassword)}
                  className="bg-zinc-100 hover:bg-white text-black font-black py-3 px-8 rounded-xl uppercase tracking-widest text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Actualizar contraseña
                </button>
                {chgMsg && (
                  <p className={`text-sm mt-3 ${chgMsg.includes("actualizada") ? "text-green-400" : "text-amber-200"}`}>
                    {chgMsg}
                  </p>
                )}
              </div>
            </form>
          </section>
        </div>
      </div>
    </div>
  );
}
